import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { TaskStatus } from '../../../../types';
import { useTaskStore } from '../../../../stores/taskStore';
import { useAuthStore } from '../../../../stores/authStore';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { getTeamMemberDisplayName } from '../../../../utils/userUtils';
import { useDialog } from '../../../../hooks/useDialog';
import { InlineCalendar } from '../../../../components/ui/InlineCalendar';
import { isTextTooLong, getDisplayLength } from '../../../../utils/userUtils';
import { AutoExpandTextarea } from '../../../../components/ui/AutoExpandTextarea';
import { ModalShell } from '../../../../components/ui/ModalShell';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  status?: TaskStatus;
}

export const QuickAddModal: React.FC<QuickAddModalProps> = ({ 
  isOpen, 
  onClose, 
  status = 'TODO' 
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [selectedTaskType, setSelectedTaskType] = useState('');
  const [selectedSize, setSelectedSize] = useState('MEDIUM');
  const [selectedPriority, setSelectedPriority] = useState('MEDIUM');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { taskTypes, createTask, teamMembers, loadTeamMembers, selectedGoalId } = useTaskStore();
  const { user } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const { alert } = useDialog();
  
  // 在个人空间时应该使用空的团队成员列表
  const effectiveTeamMembers = currentWorkspace.type === 'personal' ? [] : teamMembers;
  const titleInputRef = useRef<HTMLInputElement>(null);

  // 重置表单
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setAcceptance('');
    setSelectedTaskType('');
    setSelectedSize('MEDIUM');
    setSelectedPriority('MEDIUM');
    setSelectedAssignee('');
    setDueDate('');
  };

  // 自动聚焦到标题输入框
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // 加载团队成员（仅在团队空间时）
  useEffect(() => {
    if (currentWorkspace.type === 'team' && currentWorkspace.teamId && isOpen) {
      loadTeamMembers(currentWorkspace.teamId);
    }
  }, [currentWorkspace.type, currentWorkspace.teamId, isOpen, loadTeamMembers]);


  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!title.trim() || isSubmitting) return;

    // 仅提示标题长度，不阻止保存
    // if (isTextTooLong(title)) {
    //   await alert({
    //     title: '提示',
    //     message: '任务标题过长！中文字符不超过18个，英文字符不超过36个。',
    //     type: 'warning'
    //   });
    //   return;
    // }

    try {
      setIsSubmitting(true);
      const taskData = {
        title: title.trim(),
        description: description.trim() || undefined,
        acceptance: acceptance.trim() || undefined,
        status,
        priority: selectedPriority as 'HIGH' | 'MEDIUM' | 'LOW',
        size: selectedSize as 'SMALL' | 'MEDIUM' | 'LARGE',
        taskDate: new Date().toISOString(),
        taskTypeId: selectedTaskType || undefined,
        // 个人空间：自动分配给当前用户；团队空间：按选择分配
        userId: currentWorkspace.type === 'personal'
          ? user?.id || null
          : (selectedAssignee === '' ? null : selectedAssignee),
        dueDate: dueDate ? new Date(dueDate + 'T00:00:00.000Z').toISOString() : undefined,
        // 自动关联到当前选中的目标（如果有选中具体目标的话）
        goalId: selectedGoalId && selectedGoalId !== 'unlinked' ? selectedGoalId : undefined,
      };

      await createTask(taskData);
      handleClose();
    } catch (error) {
      console.error('创建任务失败:', error);
      await alert({
        title: '错误',
        message: '创建任务失败，请检查网络连接和权限',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [title, isSubmitting, selectedTaskType, taskTypes, description, acceptance, status, selectedPriority, selectedSize, currentWorkspace.type, user?.id, selectedAssignee, dueDate, createTask, alert]);

  // 全局键盘事件监听 - 使用useEffect监听键盘事件
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'Enter' && e.ctrlKey && !isSubmitting) {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleGlobalKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isOpen, isSubmitting, handleSave, handleClose]);

  // 标题输入框的键盘事件处理器
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  // 描述和其他输入框的键盘事件处理器
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const handleTaskTypeClick = (taskTypeId: string) => {
    setSelectedTaskType(prev => prev === taskTypeId ? '' : taskTypeId);
  };

  const handleSizeClick = (size: string) => {
    setSelectedSize(size);
  };

  const handlePriorityClick = (priority: string) => {
    setSelectedPriority(priority);
  };

  const handleAssigneeClick = (userId: string) => {
    setSelectedAssignee(prev => prev === userId ? '' : userId);
  };

  if (!isOpen) return null;

  const optionClass = (selected: boolean) =>
    `text-sm px-3.5 py-1.5 rounded-md border transition-colors ${
      selected ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 hover:bg-gray-50'
    }`;

  return (
    <ModalShell
      title="新建任务"
      onClose={handleClose}
      closeOnOverlay={false}
      footer={
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            className="text-sm px-4 py-1.5 rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
            onClick={handleClose}
          >
            取消 (ESC)
          </button>
          <button
            type="button"
            className={`text-sm px-4 py-1.5 rounded transition-colors ${
              title.trim() && !isSubmitting
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleSave}
            disabled={!title.trim() || isSubmitting}
          >
            {isSubmitting ? '创建中...' : '创建 (Ctrl+Enter)'}
          </button>
        </div>
      }
    >
      <div className="quick-add-form px-8 pb-6">
        {/* 标题输入 */}
        <div className="mb-5">
          <label className="text-xs text-gray-500 block mb-1.5">标题</label>
          <input
            ref={titleInputRef}
            type="text"
            className={`w-full text-base rounded-md border border-gray-200 bg-white px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 ${
              isTextTooLong(title) ? 'text-red-600' : ''
            }`}
            placeholder="任务标题..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            autoComplete="off"
          />
          <div className="flex justify-between items-center mt-1">
            <div className={`text-xs ${isTextTooLong(title) ? 'text-red-500' : 'text-gray-400'}`}>
              当前长度: {getDisplayLength(title).toFixed(1)}/18
            </div>
            {isTextTooLong(title) && <div className="text-xs text-red-500">标题过长</div>}
          </div>
        </div>

        {/* 描述输入 */}
        <div className="mb-5">
          <label className="text-xs text-gray-500 block mb-1.5">描述（支持 Markdown）</label>
          <AutoExpandTextarea
            value={description}
            onChange={setDescription}
            onKeyDown={handleInputKeyDown}
            placeholder="添加描述（可选）..."
            maxLength={20000}
            minRows={5}
            maxRows={30}
            focusRows={8}
          />
        </div>

        {/* 完成标准：agent 领任务前必须有 */}
        <div className="mb-6">
          <label className="text-xs text-gray-500 block mb-1.5">完成标准（支持 Markdown；要交给 agent 的任务必须写）</label>
          <AutoExpandTextarea
            value={acceptance}
            onChange={setAcceptance}
            onKeyDown={handleInputKeyDown}
            placeholder="例如：四个宽度截图无遮挡；登录流程能走通……"
            maxLength={20000}
            minRows={3}
            maxRows={30}
            focusRows={5}
          />
        </div>

        {/* 属性：左边选项，右边日期 */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_20rem] gap-x-10 gap-y-6 border-t border-gray-100 pt-6">
          <div className="space-y-5">
            {/* 任务类型选择 */}
            <div>
              <label className="text-xs text-gray-500 block mb-2">任务类别</label>
              <div className="flex flex-wrap gap-2">
                {taskTypes.length > 0 ? (
                  taskTypes.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      className={optionClass(selectedTaskType === type.id)}
                      onClick={() => handleTaskTypeClick(type.id)}
                      style={{
                        backgroundColor: selectedTaskType === type.id ? `${type.color}15` : undefined,
                        borderColor: selectedTaskType === type.id ? `${type.color}80` : undefined,
                        color: selectedTaskType === type.id ? type.color : undefined,
                      }}
                    >
                      {type.name}
                    </button>
                  ))
                ) : (
                  <div className="text-xs text-gray-400 italic p-2 border border-dashed border-gray-300 rounded">
                    暂无任务类型，请先创建任务类型
                  </div>
                )}
              </div>
            </div>

            {/* 任务大小选择 */}
            <div>
              <label className="text-xs text-gray-500 block mb-2">任务大小</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'SMALL', label: '小 (1-2小时)' },
                  { value: 'MEDIUM', label: '中 (半天)' },
                  { value: 'LARGE', label: '大 (1天+)' }
                ].map(size => (
                  <button
                    key={size.value}
                    type="button"
                    className={optionClass(selectedSize === size.value)}
                    onClick={() => handleSizeClick(size.value)}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 优先级选择 */}
            <div>
              <label className="text-xs text-gray-500 block mb-2">优先级</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'HIGH', label: '● 高', color: 'text-red-600' },
                  { value: 'MEDIUM', label: '● 中', color: 'text-yellow-600' },
                  { value: 'LOW', label: '● 低', color: 'text-green-600' }
                ].map(priority => (
                  <button
                    key={priority.value}
                    type="button"
                    className={optionClass(selectedPriority === priority.value)}
                    onClick={() => handlePriorityClick(priority.value)}
                  >
                    <span className={priority.color}>{priority.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 分配人选择 - 仅在团队空间显示 */}
            {currentWorkspace.type === 'team' && (
              <div>
                <label className="text-xs text-gray-500 block mb-2">分配给（要交给 agent，就选它或「未分配」）</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={optionClass(selectedAssignee === '')}
                    onClick={() => handleAssigneeClick('')}
                  >
                    未分配
                  </button>
                  {effectiveTeamMembers.map(member => (
                    <button
                      key={member.userId}
                      type="button"
                      className={optionClass(selectedAssignee === member.userId)}
                      onClick={() => handleAssigneeClick(member.userId)}
                    >
                      {getTeamMemberDisplayName(member, user?.id)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 到期时间选择 */}
          <div>
            <label className="text-xs text-gray-500 block mb-2">到期时间</label>
            <InlineCalendar value={dueDate} onChange={setDueDate} className="w-full" />
          </div>
        </div>
      </div>
    </ModalShell>
  );
};
